(() => {
  if (window.OAStorefront) {
    window.OAStorefront.initializeAll();
    return;
  }

  let navigationController;

  const parsePage = (html) => new DOMParser().parseFromString(html, 'text/html');

  const updatePageMetadata = (nextDocument) => {
    if (nextDocument.title) document.title = nextDocument.title;

    const currentCanonical = document.querySelector('link[rel="canonical"]');
    const nextCanonical = nextDocument.querySelector('link[rel="canonical"]');
    if (currentCanonical && nextCanonical) currentCanonical.href = nextCanonical.href;
  };

  const initializeInfiniteScroll = (storefront) => {
    if (storefront.dataset.oaInfiniteReady === 'true') return;

    const pagination = storefront.querySelector('[data-oa-pagination]');
    if (!pagination || !storefront.dataset.oaNextUrl) return;

    let isLoading = false;
    let observer;

    const loadNextPage = async () => {
      const nextUrl = storefront.dataset.oaNextUrl;
      if (!nextUrl || isLoading) return;

      isLoading = true;
      pagination.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(new URL(nextUrl, window.location.origin), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Unable to load products: ${response.status}`);

        const nextDocument = parsePage(await response.text());
        const nextStorefront = nextDocument.querySelector('[data-oa-storefront]');
        const nextGrid = nextStorefront?.querySelector('[data-oa-product-grid]');
        const currentGrid = storefront.querySelector('[data-oa-product-grid]');
        if (!nextStorefront || !nextGrid || !currentGrid) throw new Error('Product grid was not found.');

        nextGrid.querySelectorAll('[data-oa-product-card]').forEach((card) => currentGrid.append(card));

        storefront.dataset.oaNextUrl = nextStorefront.dataset.oaNextUrl || '';
        if (storefront.dataset.oaNextUrl) {
          pagination.href = storefront.dataset.oaNextUrl;
        } else {
          observer?.disconnect();
          pagination.remove();
        }

        const status = storefront.querySelector('[data-oa-status]');
        if (status) {
          const count = currentGrid.querySelectorAll('[data-oa-product-card]').length;
          status.textContent = `${count}件の商品を表示しています。`;
        }
      } catch (error) {
        console.error(error);
      } finally {
        isLoading = false;
        pagination.setAttribute('aria-busy', 'false');
      }
    };

    pagination.addEventListener('click', (event) => {
      event.preventDefault();
      loadNextPage();
    });

    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadNextPage();
      },
      { rootMargin: '500px 0px' }
    );
    observer.observe(pagination);
    storefront.dataset.oaInfiniteReady = 'true';
  };

  const initializeStorefront = (storefront) => {
    initializeInfiniteScroll(storefront);
  };

  const initializeAll = (root = document) => {
    if (root.matches?.('[data-oa-storefront]')) initializeStorefront(root);
    root.querySelectorAll?.('[data-oa-storefront]').forEach(initializeStorefront);
  };

  const navigateStorefront = async (target, shouldPushState = true) => {
    const currentStorefront = document.querySelector('[data-oa-storefront]');
    if (!currentStorefront) {
      window.location.assign(target);
      return;
    }

    navigationController?.abort();
    navigationController = new AbortController();
    currentStorefront.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(target, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: navigationController.signal,
      });
      if (!response.ok) throw new Error(`Unable to load collection: ${response.status}`);

      const nextDocument = parsePage(await response.text());
      const nextStorefront = nextDocument.querySelector('[data-oa-storefront]');
      if (!nextStorefront) throw new Error('Collection storefront was not found.');

      currentStorefront.replaceWith(nextStorefront);
      updatePageMetadata(nextDocument);
      initializeStorefront(nextStorefront);

      if (shouldPushState) history.pushState({ oaCollection: true }, '', target);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      window.location.assign(target);
    } finally {
      document.querySelector('[data-oa-storefront]')?.setAttribute('aria-busy', 'false');
    }
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-oa-collection-link]');
    if (
      !link ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const target = new URL(link.href, window.location.href);
    if (target.origin !== window.location.origin) return;

    event.preventDefault();
    navigateStorefront(target.href);
  });

  window.addEventListener('popstate', () => {
    if (document.querySelector('[data-oa-storefront]')) navigateStorefront(window.location.href, false);
  });

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));

  window.OAStorefront = { initializeAll, navigateStorefront };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }
})();
